import { cn } from "@/lib/utils/cn";

/**
 * Accessible on/off switch (no shadcn switch primitive is bundled). A `checked`
 * switch reads as "granted" — its on-state uses the success token so consent /
 * enabled states are legible at a glance.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  label,
  className,
  indeterminate = false,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  /** Accessible name when the switch has no visible `<label>`. */
  label?: string;
  className?: string;
  /** Partial (tri-state) cue for a "select all" master when only some are on. */
  indeterminate?: boolean;
}) {
  const partial = indeterminate && !checked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={partial ? "mixed" : checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-success" : partial ? "bg-success/40" : "bg-input",
        className,
      )}
    >
      <span
        className={cn(
          "inline-block size-5 rounded-full bg-background shadow transition-transform",
          checked
            ? "translate-x-5"
            : partial
              ? "translate-x-[0.7rem]"
              : "translate-x-0.5",
        )}
      />
    </button>
  );
}
